// @flow
import React from 'react';
import injectSheet from 'react-jss';
import { S, DARK } from '~/app/ui/tokens';
import cx from 'classnames';

type Props = {
  defaultImage: Object,
  updateDraft: Function,
  draft: Object,
  isMerging: boolean,
  numberOfRows: Number,
  width: Number,
  classes: Object
};

class ManageRows extends React.Component {
  props: Props = this.props;

  insertRow = (targetId) => {
    const { defaultImage, draft, updateDraft } = this.props;

    const newAssetIds = [];

    let res = draft.slice();

    // Get ids for new elements
    for (let i = targetId; i < targetId + 4; i++) {
      if (this.isPositionAvailable(i)) {
        newAssetIds.push(i);
      } else {
        // Element will be added in next row
        newAssetIds.push(i + 4);
      }
    }

    // Get first id
    const firstId = Math.min(...newAssetIds);

    // Increment ids and member ids for all elements getting shifted down
    res = res.map((asset) => {
      if (asset.id < firstId) {
        return asset;
      }

      const id = asset.id + 4;
      const members = (asset.members && asset.members.map(m => m + 4)) || [id];

      return {
        ...asset,
        id,
        members
      };
    });

    // Insert new elements
    newAssetIds.forEach((a) => {
      res.splice(a, 0, {
        ...defaultImage,
        id: a,
        members: [a]
      });
    });

    updateDraft(res);
  };

  removeRow = (targetId) => {
    const { draft, updateDraft } = this.props;

    let res = draft.slice();

    // Count the assets that need to be removed.
    const rowLength = res.filter(a => a.id >= targetId && a.id < (targetId + 4)).length;

    // Sort to ensure the elements are ordered by id
    res.sort((a, b) => a.id - b.id);

    // Get index of element with target id
    const t = res.indexOf(res.find(a => a.id === targetId));

    // Decrement id and member ids of subsequent elements
    res = res.map((asset) => {
      if (asset.id < targetId) {
        return asset;
      }

      const id = asset.id - 4;
      const members = (asset.members && asset.members.map(m => m - 4)) || [id];

      return {
        ...asset,
        id,
        members
      };
    });

    // Delete elements in the row
    res.splice(t, rowLength);

    updateDraft(res);
  };

  /*
    Returns true if this position can accept an element (it is not
    occupied by a merged element)
   */
  isPositionAvailable = (i) => {
    const { draft } = this.props;

    const asset = draft.find(a => a.members.includes(i));

    if (asset) {
      // True for a small asset, horizontal medium asset, and the top half of a large asset
      return (asset.id === i || asset.id === i - 1);
    }

    return true;
  };

  generateButtons = () => {
    const {
      numberOfRows, width, isMerging, classes: c
    } = this.props;
    const buttons = [];
    const unitSize = Math.floor(width / 4);

    if (isMerging) {
      return null;
    }

    for (let i = 0; i <= numberOfRows; i++) {
      const startIndex = i * 4;

      if (this.canInsertRow(startIndex)) {
        buttons.push(
          <span
            key={`add-${startIndex}`}
            className={cx(c.icon, c.addIcon)}
            style={{ top: `${(i * unitSize) - 17}px` }}
            onClick={() => this.insertRow(startIndex)}
          >
            {'+'}
          </span>
        );
      }

      if (this.canRemoveRow(startIndex)) {
        buttons.push(
          <span
            key={`remove-${startIndex}`}
            className={cx(c.icon, c.removeIcon)}
            style={{ top: `${(i * unitSize) + (unitSize / 2) - 17}px` }}
            onClick={() => this.removeRow(startIndex)}
          >
            {'-'}
          </span>
        );
      }
    }
    return buttons;
  };

  /*
    Row cannot be added if there is no valid opening
   */
  canInsertRow = (startIndex) => {
    const { draft } = this.props;

    // Get all assets which contain blocks on this row
    const rowAssets = draft.filter(a => a.members.some(b => b >= startIndex && b < (startIndex + 4)));

    // At least one item must originate on this row, or
    // target row is the last row
    return rowAssets.some(a => a.id >= startIndex) || rowAssets.length === 0;
  };

  /*
    Row can only be deleted if all elements are 'empty'
   */
  canRemoveRow = (startIndex) => {
    const { draft } = this.props;

    // Get all assets which originate on this row
    const rowAssets = draft.filter(a => a.id >= startIndex && a.id < (startIndex + 4));

    // Count members in all assets in one line.
    const reducer = (accumulator, currentValue) => accumulator + currentValue;
    const memberCounts = rowAssets.map(a => a.members.length);
    const totalMembers = memberCounts.reduce(reducer, 0);

    // Check if all assets are empty.
    const emptyAssets = rowAssets.every(a => a.url === '');


    // There must be exactly 4 members included inside the assets in a row.
    // And all assets must be empty.
    return (
      totalMembers === 4 && emptyAssets
    );
  };

  render() {
    return (
      <React.Fragment>
        { this.generateButtons() }
      </React.Fragment>
    );
  }
}

const styles = {
  icon: {
    position: 'absolute',
    fontSize: '30px',
    cursor: 'pointer'
  },
  addIcon: {
    color: 'green',
    left: '-19px',
    [DARK]: { color: S.green }
  },
  removeIcon: {
    color: 'red',
    left: '-16px',
    [DARK]: { color: S.red }
  },
};

export default injectSheet(styles)(ManageRows);
